/**
 * Helpers of the "shop" rules group (ops/shop.ts): line-by-line ports of the game functions the shop, arsenal,
 * Papa John, darts and steal handlers of new_interf call, plus the server-state channel of the steal apology.
 *
 * Every port keeps the original arithmetic, coercions and iteration order; the source location is named on each
 * function (src/game/FWP.ts, src/game/classes/new_interf.ts, src/game/runtime/as3.ts).
 */
import type { JsonValue } from '../../../shared/json';
import { stringArg } from '../args';
import { clone, int, keys } from '../as3';
import type { RuleContext } from '../context';
import { RuleError } from '../errors';
import { playerLevel, type GameDefs } from '../defs';
import type { PlayerState, WeaponCopy } from '../state';
import { perkEffect } from './battle-formulas';

// ------------------------------------------------------------------------------------------------ AS3 semantics

/** AS3 `a == b` (loose equality, e.g. a copy's `range == "3"` or `stolen == false`). */
export function looseEquals(a: unknown, b: unknown): boolean {
  // eslint-disable-next-line eqeqeq
  return (a as any) == (b as any);
}

/** Array sort option flags of AS3 (as3.ArrayFlags). */
export const ARRAY_NUMERIC = 16;
export const ARRAY_DESCENDING = 2;

function isObjectAtom(v: unknown): boolean {
  return v !== null && (typeof v === 'object' || typeof v === 'function');
}

function numCompare(x: number, y: number): number {
  const diff = x - y;
  if (diff === diff) return diff < 0 ? -1 : diff > 0 ? 1 : 0;
  if (!Number.isNaN(y)) return 1;
  if (!Number.isNaN(x)) return -1;
  return 0;
}

function strCompare(x: string, y: string): number {
  return x < y ? -1 : x > y ? 1 : 0;
}

/** avmplus ArraySort::qsort over an index permutation (unstable), copied from runtime/as3.ts qsort. */
function qsort(index: number[], compare: (a: number, b: number) => number, lo: number, hi: number): void {
  const swap = (a: number, b: number) => {
    const t = index[a];
    index[a] = index[b];
    index[b] = t;
  };
  const stk: [number, number][] = [];
  if (lo >= hi) return;
  for (;;) {
    const size = hi - lo + 1;
    if (size < 4) {
      if (size === 3) {
        if (compare(lo, lo + 1) > 0) {
          swap(lo, lo + 1);
          if (compare(lo + 1, lo + 2) > 0) {
            swap(lo + 1, lo + 2);
            if (compare(lo, lo + 1) > 0) swap(lo, lo + 1);
          }
        } else if (compare(lo + 1, lo + 2) > 0) {
          swap(lo + 1, lo + 2);
          if (compare(lo, lo + 1) > 0) swap(lo, lo + 1);
        }
      } else if (size === 2) {
        if (compare(lo, lo + 1) > 0) swap(lo, lo + 1);
      }
    } else {
      const pivot = lo + Math.floor(size / 2);
      swap(pivot, lo);
      let left = lo;
      let right = hi + 1;
      for (;;) {
        do left++;
        while (left <= hi && compare(left, lo) <= 0);
        do right--;
        while (right > lo && compare(right, lo) >= 0);
        if (right < left) break;
        swap(left, right);
      }
      swap(lo, right);
      if (right - 1 - lo >= hi - left) {
        if (lo + 1 < right) stk.push([lo, right - 1]);
        if (left < hi) {
          lo = left;
          continue;
        }
      } else {
        if (left < hi) stk.push([left, hi]);
        if (lo + 1 < right) {
          hi = right - 1;
          continue;
        }
      }
    }
    const top = stk.pop();
    if (!top) return;
    [lo, hi] = top;
  }
}

/**
 * AS3 `arr.sortOn(field, options)` for one field name without RETURNINDEXEDARRAY/UNIQUESORT: the single-field path
 * of runtime/as3.ts sortOn (non-object elements move to the end, the same unstable quicksort, so equal keys end up
 * in exactly the game's order). Sorts in place and returns `arr`.
 */
export function sortOn<T>(arr: T[], field: string, options: number): T[] {
  const len = arr.length >>> 0;
  if (len === 0 || len >= 0x10000000) return arr;
  const out: any[] = new Array(len);
  const atoms: any[] = new Array(len);
  for (let i = 0; i < len; i++) {
    out[i] = arr[i];
    if (isObjectAtom(arr[i])) atoms[i] = (arr[i] as any)[field];
  }
  let base: (a: any, b: any) => number;
  if (options & ARRAY_NUMERIC) base = (a, b) => numCompare(Number(a), Number(b));
  else if (options & 1) base = (a, b) => strCompare(String(a).toLowerCase(), String(b).toLowerCase());
  else base = (a, b) => strCompare(String(a), String(b));
  const compare = options & ARRAY_DESCENDING ? (a: any, b: any) => base(b, a) : base;
  const index: number[] = new Array(len);
  let j = len;
  let newlen = len;
  for (let i = len - 1; i >= 0; i--) {
    index[i] = i;
    if (!isObjectAtom(arr[i])) {
      j--;
      const temp = index[i];
      index[i] = index[j];
      if (!(i in arr)) {
        newlen--;
        index[j] = index[newlen];
        index[newlen] = temp;
      } else {
        index[j] = temp;
      }
    }
  }
  qsort(index, (l, r) => compare(atoms[index[l]], atoms[index[r]]), 0, j - 1);
  for (let i = 0; i < newlen; i++) arr[i] = out[index[i]];
  for (let i = newlen; i < len; i++) delete arr[i];
  return arr;
}

// ------------------------------------------------------------------------------------------------ player values

/**
 * The player's level as the client has it in memory: net_initialized (FWP) and new_interf.init_new_level set
 * `level.value = get_player_level(exp)`, while the stored `level` can be stale after a battle (ops.txt level.up), so
 * the rules recompute it from exp wherever the original reads `player.level.value`.
 */
export function currentLevel(p: PlayerState, defs: Pick<GameDefs, 'LEVEL_THRESHOLD'>): number {
  return playerLevel(defs, p.exp);
}

/** `if (player.perks[name]) v = player.perks[name]` - the raw perk level or 0 (new_interf return_*_prob). */
export function perkLevel(p: PlayerState, name: string): number {
  return p.perks && p.perks[name] ? p.perks[name] : 0;
}

/**
 * FWP.get_perk_effect: the one port in ./battle-formulas.ts (re-exported here for the shop helpers' callers). For a
 * level other than 1..3 it returns 0 (so e.g. a merchant level 4 would make everything free).
 */
export { perkEffect };

/** The shop price multiplier: `1`, or get_perk_effect('merchant') when the perk is set (new_interf 2166-2168, 5871-5874). */
export function merchantMultiplier(p: PlayerState): number {
  let m = 1;
  if (p.perks && p.perks['merchant']) m = perkEffect('merchant', p.perks.merchant);
  return m;
}

/** FWP.get_achiev_level (FWP.ts:5900-5917). */
export function achievLevel(defs: Pick<GameDefs, 'ACHIEV_DEF'>, id: unknown, value: unknown): number {
  const v = Number(value);
  const name = String(id);
  for (let i = 1; i <= defs.ACHIEV_DEF[0]; i++) {
    if (defs.ACHIEV_DEF[i].id == name) {
      let l = 0;
      while (defs.ACHIEV_DEF[i].levels[l] <= v) l++;
      return l - 1;
    }
  }
  return 0;
}

/** FWP.get_achiev_rank (FWP.ts:5919-5927): floor(sum of achievement levels / 10). */
export function achievRank(defs: Pick<GameDefs, 'ACHIEV_DEF'>, achiev: Record<string, unknown>): number {
  let sum = 0;
  for (const k of keys(achiev)) sum += achievLevel(defs, k, achiev[k as string]);
  return Math.floor(sum / 10);
}

/**
 * A copy of player.achiev with the derived counters FWP.get_achiev_bonus rewrites on every main-screen render
 * (FWP.ts:5943-5953: recipes = known recipes, career/missions/survival/boss = *_max - 1). The client computes the
 * rank from these refreshed values; the stored ones can be stale because the server state is never saved by a
 * render, so the rules derive them the same way instead of trusting the stored copies.
 */
export function refreshedAchiev(p: PlayerState, defs: Pick<GameDefs, 'CRAFT_DEF'>): Record<string, unknown> {
  const a: Record<string, unknown> = { ...p.achiev };
  let known = 0;
  for (let i = 1; i <= defs.CRAFT_DEF[0]; i++) if (p.recipes[i] == 1) known++;
  a['recipes'] = known;
  a['career'] = p.career_max - 1;
  a['missions'] = p.missions_max - 1;
  a['survival'] = p.survival_max - 1;
  a['boss'] = p.boss_max - 1;
  return a;
}

/** new_interf.get_req_params (new_interf.ts:7416-7436): the extra purchase requirement of a DEF entry. */
export function reqParamsMet(p: PlayerState, def: any, defs: Pick<GameDefs, 'ACHIEV_DEF' | 'CRAFT_DEF' | 'LEVEL_THRESHOLD'>): boolean {
  if (!def.req) return true;
  if (def.req == 'none') return true;
  if (def.req == 'level' && currentLevel(p, defs) >= def.req_amnt) return true;
  if (def.req == 'achiev' && achievRank(defs, refreshedAchiev(p, defs)) >= def.req_amnt) return true;
  if (def.req == 'career' && p.career_max >= def.req_amnt) return true;
  if (def.req == 'mission' && p.missions_max >= def.req_amnt) return true;
  return false;
}

/** FWP.get_range_upgrade (FWP.ts:5184-5323), the 'price' and 'sell_price' tables (other ranges return 0). */
export function rangeUpgrade(range: unknown, kind: 'price' | 'sell_price', wpclass: unknown = 1): number {
  const r = Number(range);
  const c = Number(wpclass);
  if (kind == 'price') {
    let k = 1;
    if (c == 1) k = 4;
    if (c == 2) k = 3;
    if (c == 3) k = 2;
    if (c == 4) k = 1.7;
    if (c == 5) k = 1.4;
    const f: Record<number, number> = { 2: 0.1, 3: 0.15, 4: 0.2, 5: 0.3, 6: 0.4, 7: 0.5, 8: 0.65, 9: 0.75, 10: 0.85 };
    if (Object.prototype.hasOwnProperty.call(f, r)) return f[r] * k;
  } else {
    const f: Record<number, number> = { 2: 1.1, 3: 1.25, 4: 1.45, 5: 1.75, 6: 2.15, 7: 2.55, 8: 3, 9: 3.5, 10: 4 };
    if (Object.prototype.hasOwnProperty.call(f, r)) return f[r];
  }
  return 0;
}

// ------------------------------------------------------------------------------------------------ categories

/** The item categories of the shop and the arsenal (new_interf shop_type). */
export type ShopCategory = 'weapons' | 'help' | 'flags' | 'rip' | 'impl' | 'objs';
export const SHOP_CATEGORIES: readonly ShopCategory[] = ['weapons', 'help', 'flags', 'rip', 'impl', 'objs'];

/**
 * DEF table and player store of each category, as every handler selects them from shop_type
 * (`if (shop_type == "weapons") { DEF = WEAPONS_DEF; store = player.weapons } ...`, e.g. new_interf 5878-5901).
 */
export const CATEGORY_TABLES: Readonly<Record<ShopCategory, { def: 'WEAPONS_DEF' | 'STIM_DEF' | 'FLAGS_DEF' | 'RIPS_DEF' | 'IMPL_DEF' | 'OBJ_DEF'; store: 'weapons' | 'stim_behave' | 'flags' | 'rip' | 'impl' | 'objs' }>> = {
  weapons: { def: 'WEAPONS_DEF', store: 'weapons' },
  help: { def: 'STIM_DEF', store: 'stim_behave' },
  flags: { def: 'FLAGS_DEF', store: 'flags' },
  rip: { def: 'RIPS_DEF', store: 'rip' },
  impl: { def: 'IMPL_DEF', store: 'impl' },
  objs: { def: 'OBJ_DEF', store: 'objs' },
};

/** The weapons get_sell_price always pays at the full DEF gold (new_interf 8240-8242). */
export const FULL_PRICE_WEAPONS = ['kenton_bazooka', 'kenton_grenade', 'void_melody'] as const;

/**
 * new_interf.get_sell_price (new_interf.ts:8206-8256) with `shop_type` = category, including its quirks: implants
 * and the three Kenton weapons pay the full DEF gold (even a stolen copy), a stolen copy otherwise 50, a DEF gold of
 * 0 pays 3000, everything else floor(rank multiplier * gold * (0.3 + Barter effect [- 0.1 when crafted])).
 */
export function sellPrice(defs: GameDefs, p: PlayerState, category: ShopCategory, id: string, range: unknown = 1, stolen: unknown = false, crafted: unknown = false): number {
  const r = Number(range);
  const isStolen = Boolean(stolen);
  const isCrafted = Boolean(crafted);
  const table: Record<string, any> = defs[CATEGORY_TABLES[category].def];
  let bar = 0;
  if (p.perks && p.perks['bar']) bar = perkEffect('bar', p.perks.bar);
  if (isCrafted) bar -= 0.1;
  if (category == 'impl') return Number(table[id].gold);
  if ((FULL_PRICE_WEAPONS as readonly string[]).includes(id)) return Number(table[id].gold);
  let mult = 1;
  if (category == 'weapons' && r > 1) mult = rangeUpgrade(r, 'sell_price', 1);
  if (isStolen) return 50;
  if (table[id].gold == 0) return 3000;
  return Math.floor(mult * table[id].gold * (0.3 + bar));
}

// ------------------------------------------------------------------------------------------------ inventory

/** A fresh weapon copy as the handlers create it (`{"range": 1, "stolen": ..., "crafted": false}`, key order kept). */
export function newCopy(stolen = false): WeaponCopy {
  return { range: 1, stolen, crafted: false };
}

/**
 * The inline "add one copy" of the buy / Papa / darts / steal handlers:
 *   if (!w[id]) w[id] = [1, copy]; else { ++w[id][0]; w[id][w[id][0]] = copy; }
 */
export function addWeaponCopy(weapons: Record<string, any[]>, id: string, copy: WeaponCopy): void {
  if (!weapons[id]) {
    weapons[id] = [1, copy];
  } else {
    ++weapons[id][0];
    weapons[id][weapons[id][0]] = copy;
  }
}

/**
 * FWP.weapon_optimisation (FWP.ts:6810-6833), in place: for EVERY weapon keeps the (cloned) copies 1..10 whose
 * range > 0, sorts them by range descending (as3.sortOn NUMERIC | DESCENDING, unstable) and rebuilds
 * `[count, {}]` + copies. Copies past index 10 are dropped, as in the original.
 */
export function weaponOptimisation(weapons: Record<string, any>): void {
  for (const k of keys(weapons)) {
    let n = 0;
    const list: any[] = [];
    for (let i = 1; i <= 10; i++) {
      const c = weapons[k][i];
      if (c) {
        if (c.range > 0) {
          list[n] = clone(c);
          n++;
        }
      }
    }
    sortOn(list, 'range', ARRAY_NUMERIC | ARRAY_DESCENDING);
    weapons[k] = {};
    weapons[k] = [n, {}];
    for (let i = 1; i <= n; i++) weapons[k][i] = list[i - 1];
  }
}

/**
 * Index (1..count) of the first copy of weapons[id] with exactly this range and stat, as finish_sell / finish_upgrade
 * search it (new_interf 5095-5104, 5147-5157: `range == R && stolen == S && crafted == C`, loose equality), or 0.
 * `stat` is the arsenal control's marker: 's' stolen, 'c' crafted, '' neither (render 1906-1913).
 */
export function findCopy(p: PlayerState, id: string, stat: string, range: number): number {
  const stolen = stat == 's';
  const crafted = stat == 'c';
  const list = p.weapons[id];
  if (!list) return 0;
  for (let i = 1; i <= list[0]; i++) {
    const c = list[i];
    if (c && looseEquals(c.range, range) && looseEquals(c.stolen, stolen) && looseEquals(c.crafted, crafted)) return i;
  }
  return 0;
}

/** The nine books of the collection sale (new_interf 5006). */
export const BOOK_IDS = [
  'obj_red_book',
  'obj_orange_book',
  'obj_yellow_book',
  'obj_green_book',
  'obj_skyblue_book',
  'obj_blue_book',
  'obj_violet_book',
  'obj_white_book',
  'obj_black_book',
] as const;

/** Gold of the collection sale of all nine books (new_interf 5019). */
export const BOOK_COLLECTION_GOLD = 3000000;

// ------------------------------------------------------------------------------------------------ buying

/** Reason codes of the group, besides the common ones of RULE_REASONS. */
export const SHOP_REASONS = [
  /** The shopkeeper does not let the player in (today_steals >= 8, new_interf.init('shop') 673-684). */
  'shop_closed',
  /** The item is not offered (not listed, craft-only, not in Papa's assortment of the day, ...). */
  'not_available',
  /** A purchase requirement other than the level (DEF.req: achievements, career, missions, ...). */
  'requirement_not_met',
  /** Papa John is not in town at this hour. */
  'papa_not_here',
  /** Papa's shop was already opened today ('shop_fail'). */
  'papa_shop_visited',
  /** Papa's shop is not open for the player today (papa.enter first). */
  'papa_shop_closed',
  /** The question to Papa was already asked today. */
  'already_asked',
  /** Stimulants, flags and graves have no sell button in the arsenal. */
  'not_sellable',
  /** Books are sold only as the collection of all nine. */
  'collect_all_books',
  /** The weapon cannot be upgraded at all (DEF.allow_upg). */
  'not_upgradable',
  /** The copy already has the top rank 10. */
  'max_rank',
  /** The next rank needs a higher Crafting perk (details.need). */
  'crafting_too_low',
  /** The flag, grave or implant is already worn. */
  'already_equipped',
  /** The object cannot be used (only books can). */
  'not_usable',
  /** The book teaches no recipe the player does not know yet ('new_recipes_fail'). */
  'no_new_recipes',
  /** No failed steal whose penalty can still be forgiven (today, with the state unchanged since the steal). */
  'no_pending_steal',
  /** This apology was already tried after the failed steal. */
  'already_tried',
] as const;

export type Currency = 'gold' | 'diam';
export const CURRENCIES: readonly Currency[] = ['gold', 'diam'];

/** Longest item id accepted in arguments (DEF keys are short identifiers). */
export const MAX_ID_LENGTH = 64;

/** An item id argument: a non-empty DEF-key-like string. */
export function idArg(o: Record<string, unknown>, field = 'id'): string {
  return stringArg(o, field, { maxLength: MAX_ID_LENGTH, pattern: /^[A-Za-z0-9_]+$/ });
}

/**
 * The shop lists the entry (new_interf render 2199-2231): weapons with level != -1, id <= 200, not 'skip_go' and
 * DEF.shown; every stimulant, grave and implant; every flag except flag_russia / flag_ussr (product change); objects
 * with DEF.show_shop.
 */
export function shopLists(category: ShopCategory, id: string, def: any): boolean {
  if (category == 'weapons') return def.level != -1 && def.id <= 200 && id != 'skip_go' && Boolean(def.shown);
  if (category == 'objs') return Boolean(def.show_shop);
  return id != 'flag_russia' && id != 'flag_ussr';
}

/**
 * `if (store[id]) { if (count >= DEF.limit) hide }` (new_interf 2389-2400 and 3900-3911): weapons count copies in
 * weapons[id][0]; a missing limit never hides.
 */
export function atLimit(category: ShopCategory, store: Record<string, any>, id: string, def: any): boolean {
  if (!store[id]) return false;
  if (category != 'weapons') return store[id] >= def.limit;
  return store[id][0] >= def.limit;
}

/**
 * Checks the buy buttons of a shop screen (new_interf render 2382-2400 for the shop, 3885-3911 for Papa):
 * `cur_element > 0 && DEF.level != 0`, then buy_gold needs round(gold * k) <= gold, DEF.level <= level and
 * get_req_params; buy_diam only round(diam * k) <= diamonds; both are hidden at the limit.
 */
export function checkBuyButtons(p: PlayerState, defs: GameDefs, category: ShopCategory, id: string, def: any, currency: Currency, k: number): { price: number } {
  if (!(def.id > 0) || def.level == 0) throw new RuleError('not_available', { id, reason: 'no_price' });
  const store: Record<string, any> = p[CATEGORY_TABLES[category].store];
  if (atLimit(category, store, id, def)) throw new RuleError('limit_reached', { id, limit: def.limit });
  const gold = Math.round(def.gold * k);
  const diam = Math.round(def.diam * k);
  if (currency == 'gold') {
    if (!(gold <= p.gold)) throw new RuleError('not_enough_gold', { have: p.gold, need: gold });
    const level = currentLevel(p, defs);
    if (!(def.level <= level)) throw new RuleError('level_too_low', { have: level, need: def.level });
    if (!reqParamsMet(p, def, defs)) throw new RuleError('requirement_not_met', { req: def.req, need: def.req_amnt });
    return { price: gold };
  }
  if (!(diam <= p.diam)) throw new RuleError('not_enough_diam', { have: p.diam, need: diam });
  return { price: diam };
}

/** Pays the price (`player.gold.value -= price` / `player.diam.value -= price`, ProtectedInt int32). */
export function pay(p: PlayerState, currency: Currency, price: number): void {
  if (currency == 'gold') p.gold = int(p.gold - price);
  else p.diam = int(p.diam - price);
}

/** `if (achiev["buys"]) ++achiev["buys"]; else achiev["buys"] = 1` (new_interf 5950-5956, 6004-6008). */
export function countBuy(p: PlayerState): void {
  if (p.achiev['buys']) ++p.achiev['buys'];
  else p.achiev['buys'] = 1;
}

// ------------------------------------------------------------------------------------------------ calendar

/** The date/month/year comparison of modify_papa / modify_last_enter: both unix times on the same local day. */
export function sameLocalDay(ctx: Pick<RuleContext, 'calendar'>, a: number, b: number): boolean {
  const x = ctx.calendar(a);
  const y = ctx.calendar(b);
  return x.date == y.date && x.month == y.month && x.year == y.year;
}

// ------------------------------------------------------------------------------------------------ server state

/**
 * Server-owned state (design D1 `serverState`): what the original kept only in the client's memory and a rule needs
 * to stay honest, e.g. the pending apology after a failed steal. The backend keeps it in the profile document next
 * to the game state, never accepts it from the client, passes it to the op through the context
 * (withServerState) and stores the `serverState` object the op returns in its result. Every op replaces only its
 * own key and returns all other keys unchanged.
 */
export type ServerState = { [key: string]: JsonValue };

/** A RuleContext carrying the server state of the player (see ServerState). */
export interface ServerStateContext extends RuleContext {
  readonly serverState?: ServerState;
}

/** `ctx` with the player's server state attached (the input of ops that read or write server state). */
export function withServerState<C extends RuleContext>(ctx: C, serverState: ServerState | undefined): C & ServerStateContext {
  return { ...ctx, serverState: serverState ?? {} };
}

/** A private deep copy of the context's server state ({} when none was attached). */
export function serverStateOf(ctx: RuleContext): ServerState {
  const s = (ctx as ServerStateContext).serverState;
  return s ? (JSON.parse(JSON.stringify(s)) as ServerState) : {};
}
